/**
 * StarredMessagesPage — Global view of all starred messages across all conversations
 * WhatsApp-style: shows message content, sender, conversation name, and timestamp.
 * Tapping a message navigates to that chat and scrolls to it.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { useGlobalStarredMessages } from '../hooks';
import { ChatAvatar } from '../components';
import { useMessagingFlow } from '@/stackflow';
import { PageLayout } from '@/components/layout';

interface GlobalStarredMessage {
  id: string;
  conversationId: string;
  content?: string | null;
  type: string;
  senderId: string;
  createdAt: string;
  sender: { id: string; name: string; avatar?: string };
  conversation: { id: string; type: string; name: string };
}

export function StarredMessagesPage() {
  const { t, i18n } = useTranslation();
  const { pop, replace } = useMessagingFlow();
  const { data: messages, isLoading } = useGlobalStarredMessages();

  const formatDate = useCallback((dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const isHe = i18n.language === 'he';

    if (diff < 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString(isHe ? 'he-IL' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    }
    if (diff < 7 * 24 * 60 * 60 * 1000) {
      return date.toLocaleDateString(isHe ? 'he-IL' : 'en-US', {
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return date.toLocaleDateString(isHe ? 'he-IL' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  }, [i18n.language]);

  const handleMessageTap = useCallback((msg: GlobalStarredMessage) => {
    // Replace StarredMessagesActivity with ChatActivity in the Stackflow stack.
    // This is an atomic swap — SpatialOverlay stays mounted, no race conditions.
    // scrollToMessageId is forwarded through ChatActivity → ChatPage for highlight + scroll.
    replace('ChatActivity', { conversationId: msg.conversationId, scrollToMessageId: msg.id }, { animate: true });
  }, [replace]);

  const topBar = (
    <div className="flex items-center gap-3 px-4 py-3">
      <button
        type="button"
        onClick={() => pop()}
        className="flex h-9 w-9 items-center justify-center rounded-full text-neutral-600 transition-colors hover:bg-white/30"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <div className="flex items-center gap-2">
        <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 24 24" fill="currentColor" stroke="none">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
        <h1 className="text-lg font-bold text-neutral-800">{t('messaging.starredMessages')}</h1>
      </div>
    </div>
  );

  return (
    <PageLayout topBar={topBar}>
      <div className="px-4 pb-4">
        {isLoading ? (
          <div className="space-y-3 pt-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-[80px] animate-pulse rounded-2xl bg-slate-100" />
            ))}
          </div>
        ) : !messages || messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-50">
              <svg className="h-7 w-7 text-yellow-300" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </div>
            <p className="text-sm font-medium text-slate-500">
              {t('messaging.noStarredMessages')}
            </p>
            <p className="mt-1 text-xs text-slate-400">
              {t('messaging.noStarredMessagesHint')}
            </p>
          </div>
        ) : (
          <div className="space-y-2 pt-2">
            {(messages as GlobalStarredMessage[]).map((msg, idx) => (
              <motion.button
                key={msg.id}
                type="button"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                onClick={() => handleMessageTap(msg)}
                className="flex w-full items-start gap-3 rounded-2xl border border-slate-100 bg-white p-4 text-start shadow-sm transition-all active:scale-[0.99]"
              >
                <div className="flex-shrink-0">
                  <ChatAvatar
                    src={msg.sender.avatar}
                    name={msg.sender.name}
                    size="h-10 w-10"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold text-slate-900">
                      {msg.sender.name}
                    </span>
                    <span className="flex-shrink-0 text-[11px] text-slate-400 tabular-nums">
                      {formatDate(msg.createdAt)}
                    </span>
                  </div>
                  <p className="mt-0.5 line-clamp-2 text-sm text-slate-600" dir="auto">
                    {msg.content || `[${msg.type}]`}
                  </p>
                  {/* Conversation context */}
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <svg className="h-3 w-3 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                    </svg>
                    <span className="text-[11px] font-medium text-slate-400">
                      {msg.conversation.name}
                    </span>
                  </div>
                </div>
                {/* Star indicator */}
                <div className="flex-shrink-0 pt-0.5">
                  <svg className="h-3.5 w-3.5 text-yellow-400" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  );
}

export default StarredMessagesPage;
